/* ============================================================
   NOWONDER — Universal Lander engine
   One module for the whole page: fixed-backdrop dissolves,
   blur-fade reveals, the floating-bottle scrub, the count,
   figure parallax and the order-pill handoff.
   Resilience contract (same as parallax-scene.js): the page is
   complete without this file — every image also exists inline,
   reveals rest visible, the FAQ is <details>, the buy form is a
   plain /cart/add form. This script only adds motion.
   ============================================================ */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = document.querySelector('.nwl-root');
  if (!root) return;
  document.documentElement.classList.add('nwl-js');

  const clamp = (t) => Math.max(0, Math.min(1, t));
  const ss = (a, b, x) => { const t = clamp((x - a) / (b - a)); return t * t * (3 - 2 * t); };

  /* ---------- backdrop stage: layers built from the blocks' own inline images ---------- */
  const stage = document.querySelector('.nwl-bstage');
  const bgBlocks = [...document.querySelectorAll('[data-nwl-bg]')];
  const layers = {};
  if (stage) {
    bgBlocks.forEach((blk) => {
      const id = blk.dataset.nwlBg;
      if (layers[id]) return;
      const src = blk.querySelector('.nwl-block__bg');
      const l = document.createElement('div');
      l.className = 'nwl-bl';
      if (src) l.appendChild(src.cloneNode(true)).removeAttribute('class');
      else l.style.background = 'var(--nwl-ivory)';
      stage.appendChild(l);
      layers[id] = l;
    });
    /* stage built — retire the inline copies so the dissolve owns the frame */
    bgBlocks.forEach((blk) => { const src = blk.querySelector('.nwl-block__bg'); if (src) src.style.visibility = 'hidden'; });
    const ivory = document.createElement('div');
    ivory.className = 'nwl-bl on';
    ivory.style.background = 'var(--nwl-ivory)';
    stage.appendChild(ivory);
    layers.__ivory = ivory;
  }
  let curBg = '__ivory';
  const setBg = (id) => {
    if (id === curBg || !stage) return;
    (layers[curBg] || layers.__ivory).classList.remove('on');
    (layers[id] || layers.__ivory).classList.add('on');
    curBg = layers[id] ? id : '__ivory';
  };

  /* ---------- orbit scrub ---------- */
  const orbit = document.querySelector('.nwl-orbit');
  let oDraw = null;
  if (orbit) {
    const pin = orbit.querySelector('.nwl-orbit__pin');
    const canvas = orbit.querySelector('canvas');
    const ctx = canvas.getContext('2d');
    const frames = JSON.parse(orbit.dataset.frames || '[]').map((u) => { const i = new Image(); i.src = u; return i; });
    const beats = [...orbit.querySelectorAll('.nwl-orbit__beat')];
    const cue = orbit.querySelector('.nwl-orbit__cue');
    let W = 0, H = 0;
    const size = () => {
      W = pin.clientWidth; H = pin.clientHeight;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size(); addEventListener('resize', size);
    oDraw = (t) => {
      const n = frames.length; if (!n) return;
      const im = frames[Math.min(n - 1, Math.floor(t * n))];
      if (!im.complete || !im.naturalWidth) return;
      const s = Math.max(W / im.naturalWidth, H / im.naturalHeight);
      const w = im.naturalWidth * s, h = im.naturalHeight * s;
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(im, (W - w) / 2, (H - h) / 2, w, h);
      if (beats[0]) beats[0].style.opacity = ss(0.04, 0.12, t) * (1 - ss(0.40, 0.52, t));
      if (beats[1]) beats[1].style.opacity = ss(0.60, 0.72, t);
      if (cue) cue.style.opacity = 1 - ss(0.5, 0.7, t);
    };
    if (reduced) { oDraw(0.5); beats.forEach((b) => (b.style.opacity = 1)); }
  }

  /* ---------- fx tracks: anatomy ---------- */
  const fxTracks = [...document.querySelectorAll('[data-nwl-fx]')];
  const lerp = (a2, b2, t) => a2 + (b2 - a2) * t;
  const runFx = (tr, t) => {
    const q = (n) => tr.querySelector('[data-el="' + n + '"]');
    if (tr.dataset.nwlFx === 'anatomy') {
      [...tr.querySelectorAll('.nwl-anat-chip')].forEach((C, i) => {
        const k = ss(0.1 + i * 0.24, 0.24 + i * 0.24, t);
        C.style.opacity = k; C.style.transform = 'translateX(' + 40 * (1 - k) + 'px)';
        C.classList.toggle('on', k > 0.6);
      });
      const b3 = q('bot');
      if (b3) b3.style.transform = 'translateX(-50%) translateY(' + lerp(3, -5, ss(0.08, 0.92, t)) + 'svh)';
    }
  };
  const fxProg = (tr) => { const r = tr.getBoundingClientRect(); const tot = tr.offsetHeight - innerHeight; return tot > 0 ? clamp(-r.top / tot) : 0.6; };
  /* reduced motion renders the landed state: disc up, card docked, chips in */
  if (reduced) fxTracks.forEach((tr) => { try { runFx(tr, 1); } catch (e) {} });

  /* ---------- count ---------- */
  const countSec = document.querySelector('.nwl-countsec');
  const countEl = countSec && countSec.querySelector('.nwl-count');
  const cnum = countSec && countSec.querySelector('[data-nwl-num]');
  const total = countSec ? parseInt(countSec.dataset.total || '1500000', 10) : 0;
  const fullStr = total.toLocaleString('en-US');

  /* ---------- parallax figures ---------- */
  const pars = [...document.querySelectorAll('.nwl-row figure img')];

  /* ---------- order-pill handoff: one order button on screen at a time ---------- */
  const pill = document.querySelector('.nwl-pill');
  const ctas = [...document.querySelectorAll('[data-nwl-cta]')];
  const hdr = document.getElementById('header-component');
  const foot = document.querySelector('footer');
  const pol = foot && foot.querySelector('a[href*="/policies/"]');
  const opts = [...document.querySelectorAll('.nwl-opt')];
  const phone = matchMedia('(max-width: 760px)');
  const ctaVisible = (el) => {
    const r = el.getBoundingClientRect();
    const top = hdr ? Math.max(0, hdr.getBoundingClientRect().bottom) : 0; /* a button under the sticky header isn't usable */
    if (r.bottom <= top + 8 || r.top >= innerHeight - (phone.matches ? 72 : 0)) return false;
    const rv = el.closest('.nwl-rv'); if (rv && !rv.classList.contains('in')) return false; /* still fading in */
    const beat = el.closest('.nwl-orbit__beat');
    return !beat || parseFloat(getComputedStyle(beat).opacity || 1) > 0.4;
  };
  /* show the pill when no order button is usable, and keep it off the footer's policy links */
  const pillTick = () => {
    if (!pill) return;
    let blocked = false;
    if (phone.matches) { /* the phone pill is a bottom band: never cover the policy links or a pack option */
      const band = innerHeight - (pill.offsetHeight + 24);
      const under = (el) => { const q = el.getBoundingClientRect(); return q.bottom > band && q.top < innerHeight; };
      blocked = (pol && under(pol)) || opts.some(under);
    }
    pill.classList.toggle('show', !ctas.some(ctaVisible) && !blocked);
  };

  if (countEl && !reduced) countEl.classList.remove('done'); /* server renders the landed state for no-JS */
  if (countEl && cnum && !reduced) {
    cnum.textContent = '0';
    const cio = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (!e.isIntersecting) return;
        cio.disconnect();
        const start = performance.now(), delay = 0, dur = 900;
        const tick = (now) => {
          const r = countEl.getBoundingClientRect();
          const sp = clamp((innerHeight * 0.88 - r.top) / (innerHeight * 0.43)); /* lands by mid-screen even on a fast flick */
          const p = Math.max(clamp((now - start - delay) / dur), sp);
          const ease = 1 - Math.pow(1 - p, 3);
          cnum.textContent = Math.floor(ease * total).toLocaleString('en-US');
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }, { rootMargin: '0px 0px -64px 0px', threshold: 1 }); /* the whole number is above the order bar */
    cio.observe(countEl);
  }

  /* ---------- main loop ---------- */
  const frame = () => {
    const mid = innerHeight * 0.5;
    let active = '__ivory';
    for (const blk of bgBlocks) {
      const r = blk.getBoundingClientRect();
      if (r.top <= mid && r.bottom > mid) { active = blk.dataset.nwlBg; break; }
    }
    setBg(active);
    if (orbit && oDraw && !reduced) {
      const r = orbit.getBoundingClientRect();
      const tot = orbit.offsetHeight - innerHeight;
      if (r.bottom > -60 && r.top < innerHeight + 60) oDraw(tot > 0 ? clamp(-r.top / tot) : 0);
    }

    fxTracks.forEach((tr) => { const r = tr.getBoundingClientRect(); if (r.bottom > -40 && r.top < innerHeight + 40) runFx(tr, fxProg(tr)); });
    pars.forEach((im) => {
      const r = im.getBoundingClientRect();
      const k = clamp((innerHeight - r.top) / (innerHeight + r.height));
      im.style.transform = `translateY(${(k - 0.5) * -24}px) scale(1.06)`;
    });
    pillTick();
    requestAnimationFrame(frame);
  };

  /* ---------- reveals ---------- */
  const rvs = [...document.querySelectorAll('.nwl-rv')];
  if (reduced) rvs.forEach((el) => el.classList.add('in'));
  else {
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    }), { threshold: 0.16 });
    rvs.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < innerHeight) el.classList.add('in'); /* above the fold: never hide what loaded visible */
      else io.observe(el);
    });
  }

  /* ---------- buy options: swap the form's variant + pill price ---------- */
  document.querySelectorAll('.nwl-opt').forEach((o) => {
    o.addEventListener('click', () => {
      document.querySelectorAll('.nwl-opt').forEach((x) => x.classList.remove('sel'));
      o.classList.add('sel');
      const form = o.closest('.nwl-buy__panel')?.querySelector('form[action*="/cart/add"]');
      if (form) {
        const idInput = form.querySelector('[name="id"]');
        const qtyInput = form.querySelector('[name="quantity"]');
        if (idInput && o.dataset.variant) idInput.value = o.dataset.variant;
        if (qtyInput && o.dataset.qty) qtyInput.value = o.dataset.qty;
      }
      if (pill && o.dataset.price) pill.textContent = 'Order — ' + o.dataset.price;
    });
  });

  if (reduced) {
    if (countEl && cnum) { cnum.textContent = fullStr; }
    /* no frame loop with Reduce Motion: keep the pill working from scroll events (capture also hears .page-wrapper scrolling) */
    if (pill) { addEventListener('scroll', pillTick, { passive: true, capture: true }); addEventListener('resize', pillTick); pillTick(); }
    return;
  }
  requestAnimationFrame(frame);
})();

/* October feedback round: bottle-section chips that slide in as they are
   reached (scrolling layout), arrow-driven scene strip, and a phone header
   that hides/returns with slack. The page stays complete without it. */
(() => {
  if (!document.querySelector('.nwl-root')) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const phone = matchMedia('(max-width: 760px)');

  /* ---------- anatomy chips: each slides in as the reader reaches it (the section no longer pins) ---------- */
  const anatChips = [...document.querySelectorAll('.nwl-anat-chip')];
  if (anatChips.length) {
    if (reduced || !('IntersectionObserver' in window)) anatChips.forEach((c) => c.classList.add('on'));
    else {
      const aio = new IntersectionObserver((es) => es.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('on'); aio.unobserve(e.target); }
      }), { rootMargin: '0px 0px -18% 0px', threshold: 0.35 });
      anatChips.forEach((c) => {
        if (c.getBoundingClientRect().top < innerHeight * 0.82) c.classList.add('on');   /* loaded in view: never hide it */
        else aio.observe(c);
      });
    }
  }

  /* ---------- scene strip: arrows move it one card at a time ---------- */
  document.querySelectorAll('[data-nwl-strip]').forEach((strip) => {
    const wrap = strip.closest('.nwl-strip-wrap');
    if (!wrap) return;
    const prev = wrap.querySelector('[data-nwl-strip-prev]');
    const next = wrap.querySelector('[data-nwl-strip-next]');
    const step = () => {
      const c = strip.querySelector('.nwl-scene');
      const gap = parseFloat(getComputedStyle(strip).columnGap) || 14;
      return c ? c.getBoundingClientRect().width + gap : strip.clientWidth * 0.8;
    };
    const sync = () => {
      const max = strip.scrollWidth - strip.clientWidth;
      wrap.classList.toggle('is-scrollable', max > 4);
      if (prev) prev.disabled = strip.scrollLeft <= 4;
      if (next) next.disabled = strip.scrollLeft >= max - 4;
    };
    const go = (dir) => {
      const cards = [...strip.querySelectorAll('.nwl-scene')];
      if (!cards.length) { strip.scrollBy({ left: dir * step(), behavior: reduced ? 'auto' : 'smooth' }); return; }
      const first = cards[0].getBoundingClientRect().left;
      const offs = cards.map((c) => c.getBoundingClientRect().left - first);   /* each card's snap position (card 1 snaps at 0) */
      const max = strip.scrollWidth - strip.clientWidth, cur = strip.scrollLeft;
      let to = dir > 0 ? offs.find((o) => o > cur + 4) : [...offs].reverse().find((o) => o < cur - 4);
      if (to === undefined) to = dir > 0 ? max : 0;
      strip.scrollTo({ left: Math.max(0, Math.min(max, to)), behavior: reduced ? 'auto' : 'smooth' });
    };
    if (prev) prev.addEventListener('click', () => go(-1));
    if (next) next.addEventListener('click', () => go(1));
    strip.addEventListener('scroll', sync, { passive: true });
    addEventListener('resize', sync);
    sync();
  });

  /* ---------- phones: the header hides on a real scroll down and returns on a real scroll up ---------- */
  {
    let hy = scrollY, acc = 0, hid = false, lt = 0;
    const unhide = () => { hid = false; acc = 0; document.documentElement.classList.remove('nwl-hdr-hide'); };
    addEventListener('scroll', () => {
      const y = scrollY, d = y - hy;
      if (!d) return;
      hy = y;
      if (!phone.matches) { if (hid) unhide(); return; }   /* listener always on, so rotating into portrait works */
      const now = performance.now();
      if (now - lt > 250) acc = 0;   /* separate thumb nudges don't add up */
      lt = now;
      acc = (d > 0) === (acc > 0) ? acc + d : d;
      if (!hid && acc > 48 && y > 160) { hid = true; document.documentElement.classList.add('nwl-hdr-hide'); }
      else if (hid && (acc < -56 || y < 80)) unhide();   /* -56: a bottom-of-page bounce doesn't pop it */
    }, { passive: true });
  }
})();

/* Learning Center source links (pull quotes): measure clicks and whether those readers buy.
   - GA4 event "learning_center_click" { article, link_location } (gtag is on the page via the Google app)
   - Shopify customer event "nwl_learning_center_click" for any custom pixel
   - Contentsquare dynamic variable, so sessions can be segmented by it
   - cart attribute "Learning Center link clicked": stays on the cart, so an order placed later
     shows which article the buyer opened (Orders → Additional details / export)
   The article opens in a new tab, so the lander tab and its cart request stay alive. */
(() => {
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element && e.target.closest('[data-nwl-lc]');
    if (!a) return;
    const article = a.dataset.nwlLc;
    try { if (typeof window.gtag === 'function') window.gtag('event', 'learning_center_click', { article, link_location: 'lander_pull_quote' }); } catch (err) {}
    try { window.Shopify?.analytics?.publish?.('nwl_learning_center_click', { article, link_location: 'lander_pull_quote' }); } catch (err) {}
    try { if (window._uxa) window._uxa.push(['trackDynamicVariable', { key: 'learning_center_click', value: article }]); } catch (err) {}
    try {
      fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Learning Center link clicked': article } }),
      }).catch(() => {});
    } catch (err) {}
  });
})();

/* Hero splash: sized and placed to the bottle's box (the bottle moves with the layout at every width). */
(() => {
  const splash = document.querySelector('.nwl-disc-splash');
  const bottle = document.querySelector('.nwl-disc-bottle');
  const clip = splash && splash.parentElement;
  if (!splash || !bottle || !clip) return;
  const H_RATIO = 1.34, LIFT = 0.037, ASPECT = 1792 / 2400;   /* splash height vs bottle height; splash centre sits slightly above the bottle's */
  const place = () => {
    const b = bottle.getBoundingClientRect(), c = clip.getBoundingClientRect();
    if (!b.height) return;
    const h = b.height * H_RATIO, w = h * ASPECT;
    const cx = b.left + b.width / 2 - c.left, cy = b.top + b.height / 2 - c.top - b.height * LIFT;
    splash.style.width = w + 'px'; splash.style.height = h + 'px';
    splash.style.transform = 'translate(' + (cx - w / 2) + 'px,' + (cy - h / 2) + 'px)';
    if (splash.complete && splash.naturalWidth) splash.classList.add('is-placed');
  };
  place();
  splash.addEventListener('load', place);
  bottle.addEventListener('load', place);
  addEventListener('resize', place);
  if ('ResizeObserver' in window) new ResizeObserver(place).observe(bottle);
})();

/* Learning Center grid at the end of the page ("Latest Articles", same section as the old Science page):
   the same click measurement as the pull-quote links, tagged link_location "lander_latest_articles". */
(() => {
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element && e.target.closest('a[href*="/blogs/learning-center"]');
    if (!a || a.closest('[data-nwl-lc], header, footer, #header-component, .menu-drawer')) return;
    if (!a.closest('[id*="__learning_center"]')) return;
    const path = new URL(a.href, location.href).pathname;
    const article = path.replace(/^\/blogs\//, '').replace(/\/$/, '') || 'learning-center';
    try { if (typeof window.gtag === 'function') window.gtag('event', 'learning_center_click', { article, link_location: 'lander_latest_articles' }); } catch (err) {}
    try { window.Shopify?.analytics?.publish?.('nwl_learning_center_click', { article, link_location: 'lander_latest_articles' }); } catch (err) {}
    try { if (window._uxa) window._uxa.push(['trackDynamicVariable', { key: 'learning_center_click', value: article }]); } catch (err) {}
    try {
      fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Learning Center link clicked': article } }),
      }).catch(() => {});
    } catch (err) {}
  }, true);
})();

